// One card per word of the analysed sentence: the word (opens the Word Explorer), its part of speech, a Listen button
// and a definition list for the reading the sentence picks. A form with several readings lists them as chips; tapping
// one shows that reading in the card. A word the lexicon does not know gets a gentle card with a way to the dictionary.

import type { FC, ReactNode } from 'react'
import type { Analysis } from '@/prakrit/lib/grammar/analyser'
import type { Role } from '@/prakrit/lib/grammar/sentence'
import type { Lang } from '@/prakrit/lib/grammar/types'
import type { VakyaParse, VakyaWord } from '@/prakrit/components/grammar/vakya/vakya-parse'
import type { RoleText } from '@/prakrit/components/grammar/vakya/vakya-labels'

import Link from 'next/link'
import { Fragment, useState } from 'react'
import { cn } from '@/lib/utils'
import { GENDER_LABEL, KRIDANTA_LABEL, TENSE_LABEL } from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritWord } from '@/prakrit/components/common/prakrit-word'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { ROLE_INFO, describeAnalysis, tableHref } from '@/prakrit/components/grammar/tools/karak-parse'
import { ListenButton } from '@/prakrit/components/grammar/vakya/listen-button'
import { isAnd } from '@/prakrit/components/grammar/vakya/vakya-parse'

import {
    BookOpen,
    Check,
    RotateCcw,
    SearchX,
    Table2
} from 'lucide-react'
import {
    caseNumberText,
    personNumberText,
    personText,
    posOf,
    readingLabel,
    roleText,
    tenseText,
    whoFromEnding
} from '@/prakrit/components/grammar/vakya/vakya-labels'

const lang = (hi: string, en: string): Lang => ({ hi, en })

/** A Prakrit head word inside a definition: stem, root or pronoun base. */
const Head: FC<{ word: string, accent?: boolean }> = ({ word, accent = false }): ReactNode => (
    <span lang='pra' className={cn('pra pra-sm', accent && 'text-saffron-strong!')}>{word}</span>
)

/** "(पढ़ना · to read)" after a head word. */
const Gloss: FC<Lang> = ({ hi, en }): ReactNode => (
    <span className='text-muted-foreground'>
        (<Bi hi={hi} en={en} layout='inline' />)
    </span>
)

/** A definition value: the Hindi term in bold, the English beside it. */
const Value: FC<Lang> = ({ hi, en }): ReactNode => (
    <Bi hi={hi} en={en} layout='inline' hiClassName='font-semibold' enClassName='text-muted-foreground' />
)

const RoleValue: FC<{ role: RoleText }> = ({ role }): ReactNode => (
    <Bi
        hi={<><span className='font-semibold'>{role.name.hi}</span> <span className='text-muted-foreground'>({role.question.hi})</span></>}
        en={<>{role.name.en} <span className='text-muted-foreground'>({role.question.en})</span></>}
        layout='inline'
    />
)

const Words: FC<{ words: string[] }> = ({ words }): ReactNode => (
    <span className='inline-flex flex-wrap items-baseline gap-x-1.5'>
        {words.map((w, k) => (
            <Fragment key={`${w}-${k}`}>
                {k > 0 && <span aria-hidden='true' className='text-muted-foreground'>+</span>}
                <PrakritWord word={w} className='pra pra-sm' />
            </Fragment>
        ))}
    </span>
)

interface Row {
    id: string
    label: Lang
    value: ReactNode
}

interface RowContext {
    /** The role from the sentence (only for the reading the sentence picks). */
    role: Role | null
    chosen: boolean
    /** The words the sentence reads as कर्ता. */
    subjects: string[]
    /** The main verb of the sentence, when it is another word. */
    verbToken: string | null
    /** For य / अ: the subjects it joins. */
    joins: string[]
}

const roleRow = (a: Analysis, ctx: RowContext): Row | null => {
    const role = roleText(a, ctx.role)

    if (!role) return null

    return {
        id: 'role',
        label: ctx.chosen ? lang('संभावित भूमिका', 'Likely role') : lang('इस अर्थ में भूमिका', 'Role in this reading'),
        value: <RoleValue role={role} />
    }
}

const mainVerbRow = (ctx: RowContext): Row | null => (ctx.verbToken
    ? { id: 'verb', label: lang('मुख्य क्रिया', 'Main verb'), value: <Words words={[ctx.verbToken]} /> }
    : null)

const rowsFor = (a: Analysis, ctx: RowContext): Row[] => {
    const rows: (Row | null)[] = []

    switch (a.pos) {
        case 'noun':
        case 'pronoun': {
            rows.push({
                id: 'stem',
                label: lang('मूल शब्द', 'Stem'),
                value: <><Head word={a.pos === 'pronoun' ? a.base : a.lemma} /> <Gloss hi={a.hi} en={a.en} /></>
            })
            rows.push({
                id: 'gender',
                label: lang('लिंग', 'Gender'),
                value: a.gender
                    ? <Value hi={GENDER_LABEL[a.gender].hi} en={GENDER_LABEL[a.gender].en} />
                    : <Value hi='तीनों लिंगों में एक जैसा' en='the same in every gender' />
            })

            if (a.pos === 'pronoun') rows.push({ id: 'person', label: lang('पुरुष', 'Person'), value: <Value {...personText(a.person)} /> })

            rows.push({ id: 'case', label: lang('विभक्ति, वचन', 'Case, number'), value: <Value {...caseNumberText(a.case, a.number)} /> })
            rows.push(roleRow(a, ctx))
            break
        }
        case 'verb':
        case 'as': {
            const who = whoFromEnding(a.person, a.number)
            const ending = lang(`अन्त से: ${who.hi}`, `the ending says: ${who.en}`)

            rows.push({ id: 'root', label: lang('धातु', 'Root'), value: <><Head word={a.lemma} accent /> <Gloss hi={a.hi} en={a.en} /></> })
            rows.push({ id: 'tense', label: lang('काल', 'Tense'), value: <Value {...tenseText(a.tense, TENSE_LABEL[a.tense])} /> })
            rows.push({ id: 'person', label: lang('पुरुष, वचन', 'Person, number'), value: <Value {...personNumberText(a.person, a.number)} /> })

            let value: ReactNode

            if (ctx.chosen && ctx.subjects.length > 0) {
                value = <span className='inline-flex flex-wrap items-baseline gap-x-1.5'><Words words={ctx.subjects} /> <Gloss {...ending} /></span>
            }
            else if (ctx.chosen) {
                value = <Value hi={`${who.hi} (कर्ता नहीं लिखा, क्रिया के अन्त से)`} en={`${who.en} (no subject written; from the verb ending)`} />
            }
            else {
                value = <Value hi={`${who.hi} (क्रिया के अन्त से)`} en={`${who.en} (from the verb ending)`} />
            }

            rows.push({ id: 'who', label: lang('कौन करता है', 'Who acts'), value })
            break
        }
        case 'kridanta':
            rows.push({ id: 'root', label: lang('धातु', 'Root'), value: <><Head word={a.lemma} accent /> <Gloss hi={a.hi} en={a.en} /></> })
            rows.push({ id: 'type', label: lang('प्रकार', 'Type'), value: <Value hi={KRIDANTA_LABEL[a.kind].hi} en={KRIDANTA_LABEL[a.kind].en} /> })
            rows.push(mainVerbRow(ctx))
            break
        case 'irregular': {
            const tag = describeAnalysis(a).tags[0]

            if (a.root) rows.push({ id: 'root', label: lang('धातु', 'Root'), value: <Head word={a.root} accent /> })

            rows.push({ id: 'meaning', label: lang('अर्थ', 'Meaning'), value: <Value hi={a.hi} en={a.en} /> })

            if (tag) rows.push({ id: 'type', label: lang('प्रकार', 'Type'), value: <Value hi={tag.hi} en={tag.en} /> })

            rows.push(mainVerbRow(ctx))
            break
        }
        case 'avyaya':
            rows.push({ id: 'meaning', label: lang('अर्थ', 'Meaning'), value: <Value hi={a.hi} en={a.en} /> })
            rows.push({ id: 'type', label: lang('प्रकार', 'Type'), value: <Value hi='अव्यय: इसका रूप कभी नहीं बदलता' en='indeclinable: its form never changes' /> })

            if (ctx.joins.length > 0) rows.push({ id: 'joins', label: lang('जोड़ता है', 'Joins'), value: <Words words={ctx.joins} /> })
            break
        case 'number':
            rows.push({ id: 'meaning', label: lang('अर्थ', 'Meaning'), value: <Value hi={a.hi} en={a.en} /> })
            rows.push({ id: 'type', label: lang('प्रकार', 'Type'), value: <Value hi='संख्या-शब्द' en='number word' /> })

            if (a.gender) rows.push({ id: 'gender', label: lang('लिंग', 'Gender'), value: <Value hi={GENDER_LABEL[a.gender].hi} en={GENDER_LABEL[a.gender].en} /> })

            rows.push(roleRow(a, ctx))
            break
        case 'dhatu':
            rows.push({ id: 'root', label: lang('धातु', 'Root'), value: <><Head word={a.lemma} accent /> <Gloss hi={a.hi} en={a.en} /></> })
            rows.push({ id: 'type', label: lang('प्रकार', 'Type'), value: <Value hi='मूल धातु, पूरा क्रिया-रूप नहीं' en='bare root, not a finished verb form' /> })
            break
        case 'stem':
            rows.push({ id: 'stem', label: lang('मूल शब्द', 'Stem'), value: <><Head word={a.lemma} /> <Gloss hi={a.hi} en={a.en} /></> })
            rows.push({ id: 'gender', label: lang('लिंग', 'Gender'), value: <Value hi={GENDER_LABEL[a.gender].hi} en={GENDER_LABEL[a.gender].en} /> })
            rows.push({ id: 'type', label: lang('प्रकार', 'Type'), value: <Value hi='मूल शब्द, बिना विभक्ति' en='bare stem, without a case ending' /> })
            break
    }

    return rows.filter((r): r is Row => r !== null)
}

const UnknownCard: FC<{ token: string, headingId: string }> = ({ token, headingId }): ReactNode => (
    <article aria-labelledby={headingId} className='flex h-full flex-col gap-3 rounded-3xl border border-dashed border-border bg-card/70 p-5 sm:p-6'>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5'>
            <h3 id={headingId} className='m-0 leading-none'>
                <span lang='pra' className='pra pra-xl'>{token}</span>
            </h3>
            <span className='inline-flex items-center rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs font-semibold text-muted-foreground'>
                <Bi hi='अज्ञात' en='Unknown' layout='inline' />
            </span>
        </div>
        <p className='flex items-center gap-2 font-semibold'>
            <SearchX className='size-4 shrink-0 text-muted-foreground' aria-hidden='true' />
            <Bi hi='अभी शब्दकोश में नहीं' en='Not in the lexicon yet' layout='inline' enClassName='text-muted-foreground' />
        </p>
        <p className='text-sm'>
            <Bi
                hi='वर्तनी जाँचिए, या शब्दकोश में इसका मूल शब्द खोजिए। हम अनुमान से कोई विश्लेषण नहीं दिखाते।'
                en='Check the spelling, or look for its stem in the dictionary. We never show a guessed analysis.'
            />
        </p>
        <Link
            href={`/vocabulary?q=${encodeURIComponent(token)}`}
            className='mt-auto inline-flex min-h-tap items-center gap-2 self-start rounded-lg px-1 text-sm font-medium text-prakrit underline-offset-4 hover:underline'
        >
            <BookOpen className='size-4' aria-hidden='true' />
            <Bi hi='शब्दकोश में खोजें' en='Search the vocabulary' layout='inline' enClassName='text-muted-foreground' />
        </Link>
    </article>
)

interface WordCardProps {
    word: VakyaWord
    index: number
    parse: VakyaParse
    /** Review state of the reading the sentence picks. */
    review: string
}

export const WordCard: FC<WordCardProps> = ({ word, index, parse, review }): ReactNode => {
    const [shown, setShown] = useState(-1)
    const headingId = `vakya-word-${index}`

    if (!word.chosen) return <UnknownCard token={word.token} headingId={headingId} />

    const current = shown >= 0 && word.readings[shown] ? word.readings[shown] : word.chosen
    const isChosen = current === word.chosen
    const pos = posOf(current)
    const rows = rowsFor(current, {
        role: isChosen ? word.role : null,
        chosen: isChosen,
        subjects: parse.words.filter(w => w.role === 'subject').map(w => w.token),
        verbToken: parse.verb >= 0 && parse.verb !== index ? parse.words[parse.verb].token : null,
        joins: isAnd(word) ? parse.joined.map(i => parse.words[i].token) : []
    })
    const note = describeAnalysis(current, word.analyses).note
    const href = tableHref(current)
    const reviewed = review === 'verified' || review === 'published'
    const count = word.readings.length
    const readingsId = `${headingId}-readings`

    return (
        <article aria-labelledby={headingId} className='flex h-full flex-col gap-4 rounded-3xl border border-border bg-card p-5 shadow-card sm:p-6'>
            <div className='flex items-start justify-between gap-3'>
                <div className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5'>
                    <h3 id={headingId} className='m-0 leading-none'>
                        <PrakritWord word={word.token} className='pra pra-xl' />
                    </h3>
                    <span className='inline-flex items-center rounded-full bg-prakrit/8 px-2.5 py-0.5 text-xs font-semibold text-prakrit'>
                        <Bi hi={pos.hi} en={pos.en} layout='inline' />
                    </span>
                    {!reviewed && <StatusBadge kind={reviewBadgeKind(review)} withEnglish={false} />}
                </div>
                <ListenButton text={word.token} tone='icon' />
            </div>

            {isChosen && word.role && (
                <p className='kids-only rounded-xl bg-saffron/10 px-3 py-2 text-sm'>
                    <Bi hi={ROLE_INFO[word.role].kids.hi} en={ROLE_INFO[word.role].kids.en} />
                </p>
            )}

            <dl className='grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-4 gap-y-2.5 text-sm'>
                {rows.map(r => (
                    <Fragment key={r.id}>
                        <dt className='text-muted-foreground'>
                            <Bi hi={r.label.hi} en={r.label.en} layout='inline' />
                        </dt>
                        <dd className='min-w-0'>{r.value}</dd>
                    </Fragment>
                ))}
            </dl>

            {note && (
                <p className='adults-only rounded-xl bg-surface-muted/70 px-3 py-2 text-xs'>
                    <Bi hi={note.hi} en={note.en} />
                </p>
            )}

            {count > 1 && (
                <div className='flex flex-col gap-2 border-t border-border pt-4'>
                    <p id={readingsId} className='text-xs font-semibold'>
                        <Bi
                            hi={`इस रूप के ${count} संभावित अर्थ हैं। वाक्य से तय कीजिए:`}
                            en={`This form has ${count} possible readings. Let the sentence decide:`}
                        />
                    </p>
                    <div role='group' aria-labelledby={readingsId} className='flex flex-wrap gap-1.5'>
                        {word.readings.map((r, k) => {
                            const pressed = r === current
                            const pick = r === word.chosen

                            return (
                                <button
                                    key={`${r.key}-${k}`}
                                    type='button'
                                    aria-pressed={pressed}
                                    onClick={() => setShown(pick ? -1 : k)}
                                    title={pick ? 'वाक्य यही अर्थ चुनता है · The reading the sentence picks' : undefined}
                                    className={cn(
                                        'inline-flex min-h-tap items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors sm:min-h-9',
                                        pressed ? 'border-foreground bg-foreground text-background' : 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5'
                                    )}
                                >
                                    {pick && <Check className='size-4 shrink-0' aria-hidden='true' />}
                                    <span lang='hi' className='hindi'>{readingLabel(r)}</span>
                                    {pick && <span className='sr-only'>(वाक्य यही अर्थ चुनता है · the reading the sentence picks)</span>}
                                </button>
                            )
                        })}
                    </div>
                    {!isChosen && (
                        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
                            <Bi
                                hi='आप दूसरा संभावित अर्थ देख रहे हैं। वाक्य जो अर्थ चुनता है, उस पर सही का निशान है।'
                                en='You are looking at another possible reading. The one the sentence picks has a tick.'
                                className='min-w-0 flex-1'
                            />
                            <button
                                type='button'
                                onClick={() => setShown(-1)}
                                className='inline-flex min-h-tap items-center gap-1.5 rounded-full px-2.5 font-semibold text-prakrit transition-colors hover:bg-saffron/10'
                            >
                                <RotateCcw className='size-4' aria-hidden='true' />
                                <Bi hi='वाक्य का अर्थ' en='Back to the sentence' layout='inline' always />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {href && (
                <Link
                    href={href}
                    className='mt-auto inline-flex min-h-tap items-center gap-2 self-start rounded-lg px-1 text-sm font-medium text-prakrit underline-offset-4 hover:underline'
                >
                    <Table2 className='size-4' aria-hidden='true' />
                    <Bi hi='पूरी तालिका' en='Full table' layout='inline' enClassName='text-muted-foreground' />
                </Link>
            )}
        </article>
    )
}
